/**
 * Inquiry Form Validation Schema - Story 7.3: Create Product Detail Page
 * Zod validation schema for product inquiry submission form
 */

import { z } from 'zod';

/**
 * Inquiry Form Validation Schema
 * Validates inquiry form fields: name, email, phone, quantity, message
 *
 * Validation Rules:
 * - Name: 2-100 characters, required
 * - Email: Valid email format, required
 * - Phone: Valid Indian phone format (+91XXXXXXXXXX or 10 digits), required
 * - Quantity: Positive integer, required
 * - Message: 10-1000 characters, required
 */
export const inquiryFormSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name cannot exceed 100 characters')
    .trim()
    .refine(
      (value) => /^[a-zA-Z\s]+$/.test(value),
      'Name can only contain letters and spaces'
    ),

  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(100, 'Email cannot exceed 100 characters')
    .trim()
    .toLowerCase(),

  phone: z
    .string()
    .min(1, 'Phone number is required')
    .trim()
    .length(10, 'Phone number must be exactly 10 digits')
    .regex(/^[6-9]\d{9}$/, 'Please enter a valid 10-digit phone number starting with 6-9'),

  quantity: z
    .number({
      required_error: 'Quantity is required',
      invalid_type_error: 'Quantity must be a number',
    })
    .int('Quantity must be a whole number')
    .positive('Quantity must be greater than 0')
    .max(999999, 'Quantity cannot exceed 999,999'),

  message: z
    .string()
    .min(10, 'Message must be at least 10 characters')
    .max(1000, 'Message cannot exceed 1000 characters')
    .trim()
    .refine(
      (value) => value.split(/\s+/).length >= 3,
      'Message must contain at least 3 words'
    ),
});

/**
 * Type inference from the inquiry form schema
 */
export type InquiryFormData = z.infer<typeof inquiryFormSchema>;

/**
 * Inquiry Submission Request Schema
 * Schema for the API request payload
 */
export const inquirySubmissionSchema = z.object({
  productId: z.string().min(1, 'Product ID is required'),
  name: inquiryFormSchema.shape.name,
  email: inquiryFormSchema.shape.email,
  phone: inquiryFormSchema.shape.phone,
  quantity: inquiryFormSchema.shape.quantity,
  message: inquiryFormSchema.shape.message,
});

/**
 * Type inference from the inquiry submission schema
 */
export type InquirySubmissionData = z.infer<typeof inquirySubmissionSchema>;

/**
 * Default form values for initialization
 */
export const defaultInquiryFormValues: Partial<InquiryFormData> = {
  name: '',
  email: '',
  phone: '',
  quantity: 1,
  message: '',
};

/**
 * Validation Helper: Format phone number for display
 * @param phone - Raw phone number string
 * @returns Formatted phone number string
 * @example
 * formatPhoneNumber('9876543210') // returns '+91 98765 43210'
 * formatPhoneNumber('+919876543210') // returns '+91 98765 43210'
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Extract the 10-digit number
  const match = cleaned.match(/(\+?91)?([6-9]\d{9})/);

  if (match) {
    const number = match[2];
    return `+91 ${number.slice(0, 5)} ${number.slice(5)}`;
  }

  return phone; // Return as-is if format doesn't match
}

/**
 * Validation Helper: Sanitize phone number for API submission
 * @param phone - Raw 10-digit phone number string
 * @returns Sanitized phone number in +91XXXXXXXXXX format
 * @example
 * sanitizePhoneNumber('9876543210') // returns '+919876543210'
 */
export function sanitizePhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/[^\d]/g, '');

  // Should always be 10 digits at this point (validated by schema)
  if (/^[6-9]\d{9}$/.test(cleaned)) {
    return `+91${cleaned}`;
  }

  return phone; // Return as-is if format doesn't match
}

/**
 * Validation Helper: Validate quantity against available stock
 * @param quantity - Requested quantity
 * @param availableStock - Available stock quantity
 * @returns Validation result with error message if invalid
 */
export function validateQuantityAgainstStock(
  quantity: number,
  availableStock?: number
): { isValid: boolean; error?: string } {
  if (!availableStock) {
    // If stock information is not available, assume valid
    return { isValid: true };
  }

  if (quantity > availableStock) {
    return {
      isValid: false,
      error: `Requested quantity (${quantity}) exceeds available stock (${availableStock})`,
    };
  }

  return { isValid: true };
}

/**
 * Validation Helper: Sanitize and validate message content
 * @param message - Raw message string
 * @returns Sanitized message string
 */
export function sanitizeMessage(message: string): string {
  return message
    .trim()
    // Remove excessive whitespace
    .replace(/\s+/g, ' ')
    // Remove any potential HTML/script tags
    .replace(/<[^>]*>/g, '')
    // Remove excessive punctuation
    .replace(/([!?.]){4,}/g, '$1$1$1');
}
